import type { StoredNotification } from '@cabgo/shared';
import { Bell, BellOff, CheckCheck, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { WebPushState } from '../../lib/webpush';
import { getNotificationIcon } from './notificationIcons';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: StoredNotification[];
  unreadCount: number;
  isMuted: boolean;
  onToggleMute: () => void;
  onMarkRead: (id: string) => Promise<void>;
  onMarkAllRead: () => Promise<void>;
  onSelectNotification: (item: StoredNotification) => void;
  webPushState: WebPushState;
  onEnableWebPush: () => Promise<void>;
  onDisableWebPush: () => Promise<void>;
}

function formatRelativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return 'เมื่อสักครู่';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} ชั่วโมงที่แล้ว`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay} วันที่แล้ว`;
  return new Date(isoDate).toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'short',
  });
}

export function NotificationDrawer({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  isMuted,
  onToggleMute,
  onMarkAllRead,
  onSelectNotification,
  webPushState,
  onEnableWebPush,
  onDisableWebPush,
}: NotificationDrawerProps) {
  const { t } = useTranslation('common');

  return (
    <>
      <div
        className={`notification-overlay ${isOpen ? 'notification-overlay--open' : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        className={`notification-drawer ${isOpen ? 'notification-drawer--open' : ''}`}
        role="dialog"
        aria-label="การแจ้งเตือน"
      >
        <div className="notification-drawer__header">
          <div className="notification-drawer__title">
            <span>การแจ้งเตือน</span>
            {unreadCount > 0 && <span className="notification-drawer__count-badge">{unreadCount}</span>}
          </div>
          <div className="notification-drawer__actions">
            <button
              type="button"
              className="notification-drawer__btn"
              onClick={onToggleMute}
              title={isMuted ? 'เปิดเสียงแจ้งเตือน' : 'ปิดเสียงแจ้งเตือน'}
            >
              {isMuted ? <BellOff size={14} /> : <Bell size={14} />}
              <span>{isMuted ? 'เปิดเสียง' : 'ปิดเสียง'}</span>
            </button>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notification-drawer__btn"
                onClick={() => void onMarkAllRead()}
                title="ทำเครื่องหมายว่าอ่านทั้งหมด"
              >
                <CheckCheck size={14} />
                <span>อ่านทั้งหมด</span>
              </button>
            )}
            <button
              type="button"
              className="notification-drawer__close"
              onClick={onClose}
              aria-label={t('close')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="notification-push-status">
          <div>
            <strong>{t('webPushTitle')}</strong>
            <span>{t(`webPushState_${webPushState}`)}</span>
          </div>
          {webPushState === 'enabled' ? (
            <button
              type="button"
              className="notification-drawer__btn"
              onClick={() => void onDisableWebPush()}
            >
              {t('webPushDisable')}
            </button>
          ) : (
            <button
              type="button"
              className="notification-drawer__btn"
              disabled={
                webPushState === 'registering' ||
                webPushState === 'unsupported' ||
                webPushState === 'unconfigured'
              }
              onClick={() => void onEnableWebPush()}
            >
              {webPushState === 'registering' ? t('webPushRegistering') : t('webPushEnable')}
            </button>
          )}
        </div>

        <div className="notification-drawer__body">
          {notifications.length === 0 ? (
            <div className="notification-empty">
              <Clock size={40} className="notification-empty__icon" />
              <p>ยังไม่มีการแจ้งเตือน</p>
            </div>
          ) : (
            notifications.map((item) => {
              const isUnread = !item.readAt;
              return (
                <button
                  type="button"
                  key={item.id}
                  className={`notification-item ${isUnread ? 'notification-item--unread' : ''}`}
                  onClick={() => onSelectNotification(item)}
                >
                  <div className="notification-item__icon">{getNotificationIcon(item.type)}</div>
                  <div className="notification-item__content">
                    <div className="notification-item__header">
                      <span className="notification-item__title">{item.title}</span>
                      <span className="notification-item__time">{formatRelativeTime(item.createdAt)}</span>
                    </div>
                    <div className="notification-item__body">{item.body}</div>
                  </div>
                  {isUnread && <span className="notification-item__dot" />}
                </button>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}
